// Shader code from external files
import fragmentShaderSource from './fragment-shader.glsl?raw';
import vertexShaderSource from './vertex-shader.glsl?raw';

export class WebGLRenderer {
  private canvas: HTMLCanvasElement;
  private gl: WebGL2RenderingContext | null = null;
  private program: WebGLProgram | null = null;

  // Textures and buffers
  private videoTexture: WebGLTexture | null = null;
  private positionBuffer: WebGLBuffer | null = null;
  private texCoordBuffer: WebGLBuffer | null = null;

  // Shader locations
  private positionLocation: number = -1;
  private texCoordLocation: number = -1;
  private imageLocation: WebGLUniformLocation | null = null;
  private maskLocation: WebGLUniformLocation | null = null;
  private textureSizeLocation: WebGLUniformLocation | null = null;
  private blurRadiusLocation: WebGLUniformLocation | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.initWebGL();
  }

  private initWebGL(): void {
    // Initialize WebGL context
    this.gl = this.canvas.getContext('webgl2', { preserveDrawingBuffer: true }) as WebGL2RenderingContext;
    if (!this.gl) {
      throw new Error('WebGL2 not supported');
    }

    // Create shader program
    const vertexShader = this.createShader(this.gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = this.createShader(this.gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!vertexShader || !fragmentShader) {
      throw new Error('Failed to create shaders');
    }

    this.program = this.createProgram(vertexShader, fragmentShader);
    if (!this.program) {
      throw new Error('Failed to create shader program');
    }

    // Get attribute and uniform locations
    this.positionLocation = this.gl.getAttribLocation(this.program, 'a_position');
    this.texCoordLocation = this.gl.getAttribLocation(this.program, 'a_texCoord');
    this.imageLocation = this.gl.getUniformLocation(this.program, 'u_image');
    this.maskLocation = this.gl.getUniformLocation(this.program, 'u_mask');
    this.textureSizeLocation = this.gl.getUniformLocation(this.program, 'u_textureSize');
    this.blurRadiusLocation = this.gl.getUniformLocation(this.program, 'u_blurRadius');

    // Create buffers
    this.positionBuffer = this.gl.createBuffer();
    this.texCoordBuffer = this.gl.createBuffer();

    // Create textures
    this.videoTexture = this.createAndSetupTexture();
  }

  private createAndSetupTexture(): WebGLTexture | null {
    const gl = this.gl;
    if (!gl) return null;

    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return texture;
  }

  private createShader(type: number, source: string): WebGLShader | null {
    const gl = this.gl;
    if (!gl) return null;

    const shader = gl.createShader(type);
    if (!shader) return null;

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
    if (success) {
      return shader;
    }

    console.error(gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }

  private createProgram(vertexShader: WebGLShader, fragmentShader: WebGLShader): WebGLProgram | null {
    const gl = this.gl;
    if (!gl) return null;

    const program = gl.createProgram();
    if (!program) return null;

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    const success = gl.getProgramParameter(program, gl.LINK_STATUS);
    if (success) {
      return program;
    }

    console.error(gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
    return null;
  }

  private setRectangle(x: number, y: number, width: number, height: number): void {
    const gl = this.gl;
    if (!gl) return;

    const x1 = x;
    const x2 = x + width;
    const y1 = y;
    const y2 = y + height;

    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        x1,
        y1, // Bottom left
        x2,
        y1, // Bottom right
        x1,
        y2, // Top left
        x1,
        y2, // Top left
        x2,
        y1, // Bottom right
        x2,
        y2, // Top right
      ]),
      gl.STATIC_DRAW
    );
  }

  public renderFrame(
    image: HTMLVideoElement,
    segmentationMask: WebGLTexture | null,
    blurAmount: number = 15,
    invertMask: boolean = false
  ): void {
    const gl = this.gl;
    if (!gl || !this.program || !image) return;

    // Set canvas and viewport dimensions
    const width = image.videoWidth;
    const height = image.videoHeight;
    this.canvas.width = width;
    this.canvas.height = height;
    gl.viewport(0, 0, width, height);

    // Clear the canvas
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // Use the shader program
    gl.useProgram(this.program);

    // Set the resolution uniform
    const resolutionLocation = gl.getUniformLocation(this.program, 'u_resolution');
    gl.uniform2f(resolutionLocation, width, height);

    // Set inversion mask uniform
    const invertLocation = gl.getUniformLocation(this.program, 'u_invertMask');
    gl.uniform1i(invertLocation, invertMask ? 1 : 0);

    // Prepare position buffer
    gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
    this.setRectangle(0, 0, width, height);
    gl.enableVertexAttribArray(this.positionLocation);
    gl.vertexAttribPointer(this.positionLocation, 2, gl.FLOAT, false, 0, 0);

    // Prepare texture coordinates buffer
    gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 1.0, 1.0, 0.0, 1.0, 1.0]),
      gl.STATIC_DRAW
    );
    gl.enableVertexAttribArray(this.texCoordLocation);
    gl.vertexAttribPointer(this.texCoordLocation, 2, gl.FLOAT, false, 0, 0);

    // Set the texture size
    gl.uniform2f(this.textureSizeLocation, width, height);

    // Set the blur radius
    gl.uniform1f(this.blurRadiusLocation, blurAmount);

    // Prepare video texture
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.videoTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    gl.uniform1i(this.imageLocation, 0);

    // Prepare mask texture
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, segmentationMask);
    gl.uniform1i(this.maskLocation, 1);

    // Draw the rectangle (two triangles)
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  public getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }
}
